import { X, FileText, CheckCircle } from '@phosphor-icons/react';
import React from 'react';
import {
  CustomerWithDriverOnboarding,
  OnboardingStepId,
} from '../../../../../../types/apiTypes';
import { useUser } from '../../../../../../utils/UserContext';

interface CertificateOfInsuranceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const CertificateOfInsuranceModal: React.FC<
  CertificateOfInsuranceModalProps
> = ({ isOpen, onClose }) => {
  const { userRecord } = useUser();

  // Check if either certificate is already completed
  const isPenskeCompleted = (userRecord as CustomerWithDriverOnboarding)
    ?.onboardingSteps?.[OnboardingStepId.PENSKE_CERTIFICATE_OF_INSURANCE];
  const isRyderCompleted = (userRecord as CustomerWithDriverOnboarding)
    ?.onboardingSteps?.[OnboardingStepId.RYDER_CERTIFICATE_OF_INSURANCE];
  const isBothCompleted = isPenskeCompleted && isRyderCompleted;

  const handleDownloadPenske = () => {
    window.open(
      'https://prod-public-images-m6zzcoyk.s3.us-east-2.amazonaws.com/Penske%20TACH%20COI%20form.pdf',
      '_blank'
    );
  };

  const handleDownloadRyder = () => {
    window.open(
      'https://prod-public-images-m6zzcoyk.s3.us-east-2.amazonaws.com/Ryder%20TACH%20COI%20Form.pdf',
      '_blank'
    );
  };

  const handleDownloadRyderCOIExample = () => {
    window.open(
      'https://prod-public-images-m6zzcoyk.s3.us-east-2.amazonaws.com/Ryder%20Rental%20COI%20Example%5B45%5D.pdf',
      '_blank'
    );
  };

  const renderContent = () => (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="flex h-[90%] max-h-[800px] w-[90%] flex-col overflow-hidden rounded-[4px] bg-white md:w-[600px] md:max-w-[600px]">
        {/* Fixed Header */}
        <div className="flex h-[56px] min-h-[56px] w-full flex-row items-center justify-between border-b border-bordergray p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">
            Truck Rental Certificates of Insurance
          </h2>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={onClose}
            weight="regular"
            size={24}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-6">
          <div className="rounded-lg border border-bordergray bg-gray-50 p-4">
            <p className="text-sm text-gray-700">
              To complete your truck rental onboarding with Penske and Ryder,
              you'll need to provide your Certificate of Insurance for each
              provider. Download the forms below and follow the instructions to
              submit them.
            </p>
          </div>

          {/* Penske Section */}
          <div className="flex flex-col gap-3 rounded-lg border border-bordergray p-4">
            <div className="flex items-center gap-2">
              {isPenskeCompleted ? (
                <CheckCircle
                  weight="fill"
                  className="text-2xl text-green-600"
                />
              ) : (
                <FileText
                  weight="regular"
                  className="text-2xl text-graysecondary"
                />
              )}
              <h3 className="text-[16px] font-semibold text-primary">
                Penske Certificate of Insurance
              </h3>
            </div>

            <p className="text-sm text-graysecondary">
              Complete your Penske truck rentals onboarding by submitting your
              certificate.
            </p>

            <button
              onClick={handleDownloadPenske}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-medium text-primary hover:bg-opacity-90"
            >
              <FileText weight="regular" size={20} />
              Download Penske Instructions
            </button>
          </div>

          {/* Penske Credit Authorization Section */}
          <div className="flex flex-col gap-3 rounded-lg border border-bordergray p-4">
            <div className="flex items-center gap-2">
              <FileText
                weight="regular"
                className="text-2xl text-graysecondary"
              />
              <h3 className="text-[16px] font-semibold text-primary">
                Penske Credit Authorization
              </h3>
            </div>

            <p className="text-sm text-graysecondary">
              A TACH Customer Support representative will email you with
              instructions on how to complete a credit authorization form for
              Penske.
            </p>
          </div>

          {/* Ryder Section */}
          <div className="flex flex-col gap-3 rounded-lg border border-bordergray p-4">
            <div className="flex items-center gap-2">
              {isRyderCompleted ? (
                <CheckCircle
                  weight="fill"
                  className="text-2xl text-green-600"
                />
              ) : (
                <FileText
                  weight="regular"
                  className="text-2xl text-graysecondary"
                />
              )}
              <h3 className="text-[16px] font-semibold text-primary">
                Ryder Certificate of Insurance
              </h3>
            </div>

            <p className="text-sm text-graysecondary">
              Complete your Ryder truck rentals onboarding by submitting your
              certificate.
            </p>

            <button
              onClick={handleDownloadRyder}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-medium text-primary hover:bg-opacity-90"
            >
              <FileText weight="regular" size={20} />
              Download Ryder Instructions
            </button>
            <button
              onClick={handleDownloadRyderCOIExample}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-medium text-primary hover:bg-opacity-90"
            >
              <FileText weight="regular" size={20} />
              Download Ryder Example COI
            </button>
          </div>

          {/* Status Message */}
          {isBothCompleted && (
            <div className="rounded-lg bg-green-50 p-4">
              <p className="text-sm text-green-800">
                ✓ You have completed both certificate of insurance submissions!
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="rounded-lg bg-blue-50 p-4">
            <h4 className="mb-2 text-sm font-semibold text-blue-900">
              Next Steps:
            </h4>
            <ol className="list-inside list-decimal space-y-1 text-sm text-blue-800">
              <li>Download the instruction documents above</li>
              <li>
                Follow the instructions in each PDF to submit your Certificates
                of Insurance to TACH Support
              </li>
              <li>
                A TACH representative will email you separately with
                instructions for the Penske Credit Authorization form
              </li>
              <li>
                Once submitted for both Penske and Ryder, this step will be
                marked as complete
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return <>{renderContent()}</>;
};

export default CertificateOfInsuranceModal;
